import type { DataTableColumn } from '@wundo/ui';
import type {
  UsageCostByGroup,
  UsageCostByOrganisation,
  UsageCostByProduct,
  UsageCostByUser,
  UsageCostByVendor,
} from '@/lib/usageCost.ts';
import { formatCurrency } from '@/utils/currencyUtils.ts';
import {
  formatCount,
  formatQuantity,
  formatTokens,
  share,
  vendorName,
} from './costFormat.ts';

const groupLabel = (key: string | null): string => key ?? '—';

/* Token columns only mean something for a model; other products leave them blank. */
function tokenCell(row: UsageCostByProduct, value: number): string {
  return row.unit === 'tokens' ? formatTokens(value) : '—';
}

export const productColumns: DataTableColumn<UsageCostByProduct>[] = [
  {
    key: 'product',
    header: 'Product',
    render: (row) => (
      <div>
        <div className='font-semibold text-ink'>{row.product}</div>
        <div className='text-xs text-ink-muted'>{vendorName(row.vendor)}</div>
      </div>
    ),
  },
  {
    key: 'cost_usd',
    header: 'Cost',
    align: 'end',
    nowrap: true,
    render: (row) => formatCurrency(row.cost_usd),
  },
  {
    key: 'calls',
    header: 'Uses',
    align: 'end',
    nowrap: true,
    render: (row) => formatCount(row.calls),
  },
  {
    key: 'quantity',
    header: 'Quantity',
    align: 'end',
    nowrap: true,
    render: (row) => formatQuantity(row.quantity, row.unit),
  },
  {
    key: 'input_tokens',
    header: 'Input',
    align: 'end',
    nowrap: true,
    render: (row) => tokenCell(row, row.input_tokens),
  },
  {
    key: 'output_tokens',
    header: 'Output',
    align: 'end',
    nowrap: true,
    render: (row) => tokenCell(row, row.output_tokens),
  },
  {
    key: 'cache_read_tokens',
    header: 'Cache read',
    align: 'end',
    nowrap: true,
    render: (row) => tokenCell(row, row.cache_read_tokens),
  },
  {
    key: 'cache_creation_tokens',
    header: 'Cache write',
    align: 'end',
    nowrap: true,
    render: (row) => tokenCell(row, row.cache_creation_tokens),
  },
];

export const userColumns: DataTableColumn<UsageCostByUser>[] = [
  {
    key: 'name',
    header: 'User',
    render: (row) => (
      <div>
        <div className='font-semibold text-ink'>
          {row.name ?? row.email ?? 'Removed user'}
        </div>
        {row.name && row.email && (
          <div className='text-xs text-ink-muted'>{row.email}</div>
        )}
      </div>
    ),
  },
  {
    key: 'organisation_name',
    header: 'Organisation',
    render: (row) => row.organisation_name ?? '—',
  },
  {
    key: 'cost_usd',
    header: 'Cost',
    align: 'end',
    nowrap: true,
    render: (row) => formatCurrency(row.cost_usd),
  },
];

export const groupColumns: DataTableColumn<UsageCostByGroup>[] = [
  { key: 'key', header: 'Name', render: (row) => groupLabel(row.key) },
  {
    key: 'calls',
    header: 'Uses',
    align: 'end',
    nowrap: true,
    render: (row) => formatCount(row.calls),
  },
  {
    key: 'cost_usd',
    header: 'Cost',
    align: 'end',
    nowrap: true,
    render: (row) => formatCurrency(row.cost_usd),
  },
];

/** The vendors, each with its share of `totalCost`. */
export function vendorColumns(totalCost: number): DataTableColumn<UsageCostByVendor>[] {
  return [
    { key: 'vendor', header: 'Vendor', render: (row) => vendorName(row.vendor) },
    {
      key: 'cost_usd',
      header: 'Cost',
      align: 'end',
      nowrap: true,
      render: (row) => formatCurrency(row.cost_usd),
    },
    {
      key: 'calls',
      header: 'Uses',
      align: 'end',
      nowrap: true,
      render: (row) => formatCount(row.calls),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      align: 'end',
      nowrap: true,
      render: (row) => formatQuantity(row.quantity, row.unit),
    },
    {
      key: 'share',
      header: 'Share',
      align: 'end',
      nowrap: true,
      render: (row) => share(row.cost_usd, totalCost),
    },
  ];
}

/** The organisations, each with its share of `totalCost`. */
export function organisationColumns(
  totalCost: number,
): DataTableColumn<UsageCostByOrganisation>[] {
  return [
    {
      key: 'organisation_name',
      header: 'Organisation',
      render: (row) =>
        row.organisation_name ??
        (row.organisation_id ? 'Deleted org' : 'No organisation'),
    },
    {
      key: 'cost_usd',
      header: 'Cost',
      align: 'end',
      nowrap: true,
      render: (row) => formatCurrency(row.cost_usd),
    },
    {
      key: 'calls',
      header: 'Uses',
      align: 'end',
      nowrap: true,
      render: (row) => formatCount(row.calls),
    },
    {
      key: 'share',
      header: 'Share',
      align: 'end',
      nowrap: true,
      render: (row) => share(row.cost_usd, totalCost),
    },
  ];
}
